import React, { useState, useEffect, useContext } from 'react';
import './DemoBookingPage.css';
import Footer from "../Footer";
import AdminNav from "../Navbars/AdminNav";
import { UserContext } from "../UserContext";
import { useNavigate } from "react-router-dom";
import { countries } from 'countries-list';
import moment from 'moment-timezone';

const DemoBookingPage = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    studentName: '',
    age: '',
    phoneNumber: '',
    whatsappNumber: '',
    email: '',
    country: '',
    timezone: '',
    course: '',
    date: '',
    timeSlots: [],
    message: '',
    subscribe: false
  });

  const [courses, setCourses] = useState([]);
  const [isLoadingCountry, setIsLoadingCountry] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const { isAdmin, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Time slot options in UTC (will be converted to local time)
  const timeSlotOptions = [
    '10:00-12:00',
    '12:00-14:00',
    '14:00-16:00',
    '16:00-18:00'
  ];

  // Fetch courses from backend
  const fetchCourses = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/courses`);
      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }
      const data = await response.json();
      setCourses(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
      // Fallback courses if API fails
      setCourses([
        'Coding Fundamentals',
        'Game Development',
        'Robotics',
        'Creative Arts',
        'Math Adventures',
        'Science Explorers'
      ]);
    }
  };

  // Format time with timezone
  const formatTimeWithTimezone = (timeRange) => {
    if (!formData.timezone) return timeRange.replace('-', ' to ');

    try {
      const [start, end] = timeRange.split('-');
      const startTime = moment.tz(`2000-01-01T${start}:00`, formData.timezone);
      const endTime = moment.tz(`2000-01-01T${end}:00`, formData.timezone);

      return `${startTime.format('h:mm A')} to ${endTime.format('h:mm A')} (${moment.tz(formData.timezone).format('z')})`;
    } catch (e) {
      console.error("Error formatting time:", e);
      return timeRange.replace('-', ' to ');
    }
  };

  // Get country name from country code
  const getCountryName = (countryCode) => {
    return countries[countryCode]?.name || countryCode;
  };

  // Get country data from country name
  const getCountryData = (countryName) => {
    const countryEntry = Object.entries(countries).find(
      ([, data]) => data.name === countryName
    );

    if (!countryEntry) return null;

    const [code, data] = countryEntry;
    const timezones = moment.tz.zonesForCountry(code);

    return {
      code: `+${data.phone}`,
      timezone: timezones.length > 0 ? timezones[0] : 'UTC'
    };
  };

  // Fetch user info and courses on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/user`, {
          credentials: "include",
        });
        const data = await response.json();
        setUserInfo(data);
        if (data?.usertype) {
          setUserRole(data.usertype);
        }
      } catch (error) {
        console.log("Error in fetchUserInfo:", error);
      }
    };

    fetchUserInfo();
    fetchCourses();
  }, [setUserInfo, setUserRole]);

  // Detect user's country from IP
  useEffect(() => {
    const detectCountry = async () => {
      try {
        let response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('First API failed');

        let data = await response.json();
        const countryName = getCountryName(data.country);
        const countryData = getCountryData(countryName);

        if (countryName && countryData) {
          setFormData(prev => ({
            ...prev,
            country: countryName,
            timezone: countryData.timezone,
            phoneNumber: countryData.code,
            whatsappNumber: countryData.code
          }));
        }
      } catch (firstError) {
        console.log("First country detection failed, trying fallback...");
        try {
          const fallbackResponse = await fetch('https://geolocation-db.com/json/');
          const fallbackData = await fallbackResponse.json();
          const countryName = getCountryName(fallbackData.country_code);
          const countryData = getCountryData(countryName);

          if (countryName && countryData) {
            setFormData(prev => ({
              ...prev,
              country: countryName,
              timezone: countryData.timezone,
              phoneNumber: countryData.code,
              whatsappNumber: countryData.code
            }));
          }
        } catch (fallbackError) {
          console.error("Could not detect country:", fallbackError);
        }
      } finally {
        setIsLoadingCountry(false);
      }
    };

    detectCountry();
  }, []);

  // Handle logout
  const onLogout = () => {
    fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
      credentials: "include",
      method: "POST",
    })
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
    setUserInfo(null);
    setUserRole("guest");
  };

  // Handle time slot checkbox changes
  const handleTimeSlotChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const newTimeSlots = checked
        ? [...prev.timeSlots, value]
        : prev.timeSlots.filter(slot => slot !== value);

      return {
        ...prev,
        timeSlots: newTimeSlots
      };
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const [generatedOtp, setGeneratedOtp] = useState('');

  const handleSendOtp = async () => {
    if (!validateEmail(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    try {
      const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
      setGeneratedOtp(newOtp);

      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/sendEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otp: newOtp
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send OTP');
      }

      const data = await response.json();
      setShowOtpInput(true);
      alert('OTP sent successfully!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert('Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      alert('Please enter a valid 6-digit OTP');
      return;
    }

    if (otp === generatedOtp) {
      setIsEmailVerified(true);
      setShowOtpInput(false);
      alert('Email verified successfully!');
    } else {
      alert('Invalid OTP. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'email') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Reset verification when email changes
      setIsEmailVerified(false);
      setShowOtpInput(false);
      setOtp('');
      return;
    }

    if (name === 'phoneNumber' || name === 'whatsappNumber') {
      const currentValue = formData[name];
      const hasCountryCode = /^\+\d+/.test(currentValue);

      if (hasCountryCode) {
        if (value.length < currentValue.length && value.length <= currentValue.match(/^\+\d+/)[0].length) {
          return;
        }

        if (!value.startsWith(currentValue.match(/^\+\d+/)[0])) {
          setFormData(prev => ({
            ...prev,
            [name]: `${currentValue.match(/^\+\d+/)[0]} ${value.replace(/^\+\d+\s?/, '')}`
          }));
          return;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.timeSlots.length === 0) {
      alert('Please select at least one time slot');
      return;
    }

    if (!isEmailVerified) {
      alert('Please verify your email first');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/bookDemo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          course: formData.course 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to book demo class');
      }

      alert('Demo class booked successfully! We will contact you soon.');

      const currentCountry = formData.country;
      const countryData = getCountryData(currentCountry) || { code: '', timezone: 'UTC' };

      setFormData({
        parentName: '',
        studentName: '',
        age: '',
        phoneNumber: countryData.code,
        whatsappNumber: countryData.code,
        email: '',
        country: currentCountry,
        timezone: countryData.timezone,
        course: '',
        date: '',
        timeSlots: [],
        message: '',
        subscribe: false
      });
      setIsEmailVerified(false);
      setShowOtpInput(false);
      setOtp('');
      setGeneratedOtp('');

    } catch (error) {
      console.error('Error booking demo:', error);
      alert(error.message || 'Failed to book demo class. Please try again.');
    }
  };

  const username = userInfo?.username;

  return (
    <>
      {username && isAdmin && <AdminNav username={username} onLogout={onLogout} />}
      <div className="demo-booking-container">
        <div className="demo-booking-header">
          <h1>Book a Fun Demo Class!</h1>
          <p>Let your child explore the joy of learning with our expert educators</p>
          <div className="header-decoration">
            <div className="decoration-star">⭐</div>
            <div className="decoration-star">🌟</div>
            <div className="decoration-star">✨</div>
          </div>
        </div>

        <div className="demo-booking-form-container">
          <form onSubmit={handleSubmit} className="demo-booking-form">
            <div className="form-section">
              <h2 className="section-title">👨‍👩‍👧‍👦 Student Info</h2>
              <div className="form-group">
                <label htmlFor="studentName">Student's Name*</label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleChange}
                  required
                  placeholder="Enter child's full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="parentName">Parent's Name*</label>
                <input
                  type="text"
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  required
                  placeholder="Enter parent's full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="age">Student's Age*</label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  min="3"
                  max="18"
                  placeholder="Age between 3-18"
                />
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">📱 Contact Details</h2>
              <div className="form-group">
                <label htmlFor="country">Country*</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder={isLoadingCountry ? "Detecting your country..." : "Enter your country"}
                  disabled={true}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number*</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  placeholder="Enter phone number with country code"
                />
                {formData.phoneNumber && (
                  <small className="hint-text">Country code automatically added based on your location</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="whatsappNumber">WhatsApp Number (optional)</label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  placeholder="Enter WhatsApp number if different"
                />
                {formData.whatsappNumber && (
                  <small className="hint-text">Country code automatically added based on your location</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address*</label>
                <div className="email-verification-container">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                    className={isEmailVerified ? 'verified' : ''}
                  />
                  {!isEmailVerified && (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={!validateEmail(formData.email) || isSendingOtp}
                      className="send-otp-button"
                    >
                      {isSendingOtp ? 'Sending...' : 'Send OTP'}
                    </button>
                  )}
                </div>
                {showOtpInput && (
                  <div className="otp-input-container">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="otp-input"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otp.length !== 6}
                      className="verify-otp-button"
                    >
                      Verify OTP
                    </button>
                  </div>
                )}
                {isEmailVerified && (
                  <small className="success-text">✓ Email verified successfully</small>
                )}
              </div>
            </div>

            <div className="form-section">
              <h2 className="section-title">📚 Class Details</h2>
              <div className="form-group">
                <label htmlFor="course">Select Course*</label>
                <select
                  id="course"
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  required
                  disabled={!isEmailVerified}
                  className={!isEmailVerified ? 'disabled-field' : ''}
                >
                  <option value="">Choose a course</option>
                  {courses.map((course) => (
                    <option key={course.Course_id} value={course.Course_id}>
                      {course.coursename}
                    </option>
                  ))}
                </select>
                {!isEmailVerified && (
                  <small className="hint-text">Please verify your email first</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="date">Preferred Date*</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  disabled={!isEmailVerified}
                  className={!isEmailVerified ? 'disabled-field' : ''}
                />
                {!isEmailVerified && (
                  <small className="hint-text">Please verify your email first</small>
                )}
              </div>

              <div className="form-group">
                <label>Preferred Time Slots*</label>
                <div className="time-slot-options">
                  {timeSlotOptions.map((slot, index) => (
                    <div key={index} className="time-slot-option">
                      <input
                        type="checkbox"
                        id={`timeSlot-${index}`}
                        name="timeSlots"
                        value={slot}
                        checked={formData.timeSlots.includes(slot)}
                        onChange={handleTimeSlotChange}
                        disabled={!isEmailVerified}
                        className={!isEmailVerified ? 'disabled-field' : ''}
                      />
                      <label htmlFor={`timeSlot-${index}`} className={!isEmailVerified ? 'disabled-label' : ''}>
                        {formatTimeWithTimezone(slot)}
                      </label>
                    </div>
                  ))}
                </div>
                {!isEmailVerified && (
                  <small className="hint-text">Please verify your email first</small>
                )}
                {formData.timezone && (
                  <small className="hint-text">
                    Times shown in your local timezone: {moment.tz(formData.timezone).format('z')} ({formData.timezone})
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="message">Any Message?</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Let us know if your child has any special needs or interests"
                  rows="3"
                  disabled={!isEmailVerified}
                  className={!isEmailVerified ? 'disabled-field' : ''}
                />
                {!isEmailVerified && (
                  <small className="hint-text">Please verify your email first</small>
                )}
              </div>
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="subscribe"
                name="subscribe"
                checked={formData.subscribe}
                onChange={handleChange}
                disabled={!isEmailVerified}
                className={!isEmailVerified ? 'disabled-field' : ''}
              />
              <label htmlFor="subscribe" className={!isEmailVerified ? 'disabled-label' : ''}>
                Subscribe to our newsletter for fun learning tips and offers!
              </label>
              {!isEmailVerified && (
                <small className="hint-text">Please verify your email first</small>
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={!isEmailVerified}
            >
              Book Demo Class Now!
            </button>
          </form>

          <div className="form-decoration">
            <div className="decoration-item crayon">🖍️</div>
            <div className="decoration-item robot">🤖</div>
            <div className="decoration-item book">📚</div>
          </div>
        </div>

        <div className="demo-booking-footer">
          <p>We can't wait to meet your little learner!</p>
          <div className="footer-decoration">
            <span>🎨</span>
            <span>🧩</span>
            <span>🔬</span>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DemoBookingPage;